import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '@/shared/api/api-error';

import { useCreateMappings, useProviderOptions } from '../api/providers.queries';
import { toFormPath, toMappingPayload } from '../lib/mapping-form';
import type { MintrouteCatalogueNames } from '../lib/mintroute-details';
import {
  EMPTY_MINTROUTE_DETAILS,
  mapProvidersSchema,
  type MapProvidersValues,
  type MappingFormValues,
} from '../schemas/mapping.schema';
import { MintrouteFields } from './mintroute-fields';

const EMPTY_ROW: MappingFormValues = {
  vendorId: 0,
  cost: Number.NaN,
  productIdentifier: '',
  isActive: true,
  identifierRequired: false,
  integration: null,
  mintroute: EMPTY_MINTROUTE_DETAILS,
};

/**
 * Map one or more providers to a denomination in one step — all or none.
 * Checkout buys each order from the cheapest usable provider, so mapping
 * several gives a fallback when one runs out of stock or balance.
 *
 * Each provider asks for its own product id: Mintroute the EAN, LikeCard its
 * product id (both required — an order routed to a mapping without one fails).
 * The label and rule come from the provider (`productIdentifierLabel`,
 * `productIdentifierRequired`), so the backend stays the one place that knows
 * the integrations. Mount only while open.
 */
export function MapProvidersDialog({
  denominationId,
  denominationLabel,
  catalogue,
  mappedVendorIds,
  onClose,
}: {
  denominationId: number;
  denominationLabel: string;
  /** Our category, brand and denomination names, copied into a Mintroute details row. */
  catalogue: MintrouteCatalogueNames;
  /** Providers already mapped to the denomination (not offered again). */
  mappedVendorIds: readonly number[];
  onClose: () => void;
}) {
  const providers = useProviderOptions();
  const create = useCreateMappings();
  // Each row's Mintroute category starts as our brand's category.
  const newRow: MappingFormValues = {
    ...EMPTY_ROW,
    mintroute: { ...EMPTY_MINTROUTE_DETAILS, category: catalogue.category ?? '' },
  };

  const form = useForm<MapProvidersValues>({
    resolver: zodResolver(mapProvidersSchema),
    defaultValues: { items: [newRow] },
  });
  const rows = useFieldArray({ control: form.control, name: 'items' });

  const { error } = create;
  useEffect(() => {
    if (!(error instanceof ApiError)) return;
    for (const [field, message] of Object.entries(error.fieldErrors ?? {})) {
      const path = toFormPath(field);
      if (path) form.setError(path, { type: 'server', message });
    }
  }, [error, form]);

  const onSubmit = (values: MapProvidersValues) => {
    create.mutate(
      {
        denominationId,
        items: values.items.map((item) => ({
          ...toMappingPayload(item),
          vendorId: item.vendorId,
          isActive: item.isActive,
        })),
      },
      { onSuccess: onClose },
    );
  };

  const items = form.watch('items');
  const unmapped = (providers.data ?? []).filter(
    (provider) => !mappedVendorIds.includes(provider.id),
  );
  const canAddRow = unmapped.length > rows.fields.length;
  const isPending = create.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Map providers</DialogTitle>
          <DialogDescription className="text-xs">
            {denominationLabel} — add every provider that sells it. The cheapest usable one supplies
            each order.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {rows.fields.map((row, index) => {
              const vendorId = items[index]?.vendorId ?? 0;
              const provider = providers.data?.find((candidate) => candidate.id === vendorId);
              // A row may keep its own provider; others already chosen are not offered.
              const options = unmapped.filter(
                (candidate) =>
                  candidate.id === vendorId ||
                  !items.some((item, other) => other !== index && item.vendorId === candidate.id),
              );

              return (
                <fieldset
                  key={row.id}
                  className="border-border grid gap-4 rounded-lg border p-3 sm:grid-cols-2"
                  aria-label={`Provider ${index + 1}`}
                >
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <FormField
                      control={form.control}
                      name={`items.${index}.vendorId`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel>Provider</FormLabel>
                          <Select
                            value={field.value ? String(field.value) : undefined}
                            onValueChange={(value) => {
                              const id = Number(value);
                              field.onChange(id);
                              const chosen = providers.data?.find((p) => p.id === id);
                              form.setValue(
                                `items.${index}.identifierRequired`,
                                chosen?.productIdentifierRequired ?? false,
                              );
                              form.setValue(
                                `items.${index}.integration`,
                                chosen?.integration ?? null,
                              );
                            }}
                            disabled={isPending || providers.isLoading}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a provider" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {options.map((option) => (
                                <SelectItem key={option.id} value={String(option.id)}>
                                  {option.name}
                                  {!option.isActive && ' — inactive'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {rows.fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-6 size-9"
                        aria-label={`Remove provider ${index + 1}`}
                        onClick={() => rows.remove(index)}
                        disabled={isPending}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name={`items.${index}.cost`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Cost{provider?.currencyCode ? ` (${provider.currencyCode})` : ''}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={Number.isNaN(field.value) ? '' : field.value}
                            onChange={(event) => field.onChange(event.target.valueAsNumber)}
                            type="number"
                            min={0}
                            step="0.0001"
                            disabled={isPending}
                          />
                        </FormControl>
                        <FormDescription>What the provider charges us.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`items.${index}.productIdentifier`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{provider?.productIdentifierLabel ?? 'Product id'}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            inputMode={provider?.productIdentifierRequired ? 'numeric' : undefined}
                            className="font-mono text-xs"
                            disabled={isPending || !provider}
                          />
                        </FormControl>
                        <FormDescription>
                          {!provider
                            ? 'Choose the provider first.'
                            : provider.productIdentifierRequired
                              ? `Required: ${provider.name} orders are bought with it.`
                              : 'Optional for this provider.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {provider?.integration === 'Mintroute' && (
                    <MintrouteFields
                      control={form.control}
                      index={index}
                      names={catalogue}
                      disabled={isPending}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name={`items.${index}.isActive`}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between gap-3 sm:col-span-2">
                        <div className="space-y-0.5">
                          <FormLabel>Use this provider now</FormLabel>
                          <FormDescription>Off keeps the mapping ready but unused.</FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isPending}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </fieldset>
              );
            })}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => rows.append(newRow)}
              disabled={isPending || !canAddRow}
            >
              <Plus className="size-3.5" aria-hidden />
              Add another provider
            </Button>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {rows.fields.length === 1 ? 'Map provider' : `Map ${rows.fields.length} providers`}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
